const STORAGE_PREFIX = "streaminal:pending-command:";

export interface PendingCommand {
  requestKey: string;
  operationId?: string;
  generation?: number;
}

const storageKey = (scope: string) => `${STORAGE_PREFIX}${scope}`;

const read = (scope: string): PendingCommand | null => {
  const raw = sessionStorage.getItem(storageKey(scope));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PendingCommand;
    if (typeof parsed.requestKey === "string") return parsed;
  } catch {
    // Records written before operation ownership was added contain only the key.
  }
  return { requestKey: raw };
};

const write = (scope: string, record: PendingCommand) =>
  sessionStorage.setItem(storageKey(scope), JSON.stringify(record));

export function checkoutCommandScope(
  action: "checkout" | "convert" | "resubscribe",
  streamId: string | null,
  plan: string,
  blocks = 0,
  acknowledgeQualityLoss = false
): string {
  return [
    "billing:checkout",
    action,
    streamId ?? "new",
    plan,
    blocks,
    acknowledgeQualityLoss ? "ack" : "no-ack",
  ].join(":");
}

export function beginCommand(scope: string): PendingCommand {
  const existing = read(scope);
  if (existing) return existing;
  const generationKey = `${STORAGE_PREFIX}generation`;
  const generation = Number(sessionStorage.getItem(generationKey) ?? "0") + 1;
  sessionStorage.setItem(generationKey, String(generation));
  const record = { requestKey: crypto.randomUUID(), generation };
  write(scope, record);
  return record;
}

export function getCommand(scope: string): PendingCommand | null {
  return read(scope);
}

export function bindCommandOperation(
  scope: string,
  requestKey: string,
  operationId: string
): boolean {
  const current = read(scope);
  if (!current || current.requestKey !== requestKey) return false;
  write(scope, { ...current, operationId });
  return true;
}

export function finishCommand(
  scope: string,
  expected: Partial<PendingCommand> = {}
): boolean {
  const current = read(scope);
  if (!current) return false;
  if (expected.requestKey && current.requestKey !== expected.requestKey)
    return false;
  if (expected.operationId && current.operationId !== expected.operationId)
    return false;
  sessionStorage.removeItem(storageKey(scope));
  return true;
}

export function listCommands(scopePrefix = ""): Array<{
  scope: string;
  record: PendingCommand;
}> {
  const found: Array<{ scope: string; record: PendingCommand }> = [];
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (key === `${STORAGE_PREFIX}generation`) continue;
    if (!key?.startsWith(`${STORAGE_PREFIX}${scopePrefix}`)) continue;
    const scope = key.slice(STORAGE_PREFIX.length);
    const record = read(scope);
    if (record) found.push({ scope, record });
  }
  return found.sort((left, right) => left.scope.localeCompare(right.scope));
}

export function finishOlderCommands(
  ownerScope: string,
  requestKey: string,
  relatedScopePrefixes: string[]
): void {
  const owner = read(ownerScope);
  if (!owner || owner.requestKey !== requestKey) return;
  for (const { scope, record } of listCommands()) {
    if (
      scope !== ownerScope &&
      relatedScopePrefixes.some((prefix) => scope.startsWith(prefix)) &&
      (record.generation ?? 0) < (owner.generation ?? 0)
    )
      finishCommand(scope, { requestKey: record.requestKey });
  }
}

export function finishCommandsForOperation(
  operationId: string,
  streamId?: string
): void {
  for (const { scope, record } of listCommands()) {
    // Checkout creation can complete while its attached payment intent remains
    // open. Only the account checkout projection may retire these commands.
    if (scope.startsWith("billing:checkout:")) continue;
    if (
      record.operationId === operationId &&
      (!streamId ||
        scope.endsWith(`:${streamId}`) ||
        scope.includes(`:${streamId}:`))
    )
      finishCommand(scope, { operationId });
  }
}

export function commandScopeForOperation(operationId: string): string | null {
  return (
    listCommands().find(({ record }) => record.operationId === operationId)
      ?.scope ?? null
  );
}
